/* © 2023 Promethean. All Rights Reserved.
 *
 * Unauthorized copying of this file or any part of this file
 * via any medium is strictly prohibited.
 */
import React, { useEffect } from 'react'
import { useIntl } from 'react-intl'
import classnames from 'classnames'
import { SKIP_COMMAND_RESET_ID, useRtl } from '@prom/web-embers'
import Paper from '@mui/material/Paper'
import { StyledTitledCard } from './styles'

export interface ITitledCardProps {
  className?: string
  viewMode?: 'wide' | 'narrow'
  /** The content showing above Title. */
  contentBeforeTitle?: React.ReactNode
  header?: React.ReactNode
  title?: React.ReactNode
  subtitle?: React.ReactNode
  feedbackMessage?: React.ReactNode
  description?: React.ReactNode[] | React.ReactNode
  scroll?: boolean
  onBack?: () => void
  backUrl?: string
  children?: React.ReactNode
}

export const TitledCard = (props: ITitledCardProps) => {
  const intl = useIntl()
  const { transformRTL } = useRtl()
  const {
    viewMode = 'wide',
    onBack,
    backUrl,
    header,
    title,
    contentBeforeTitle,
    subtitle,
    feedbackMessage,
    description,
    children,
    className,
  } = props

  useEffect(() => {
    const skipResetELement = document.querySelector(`#${SKIP_COMMAND_RESET_ID}`) as HTMLDivElement
    skipResetELement?.focus?.()
  }, [onBack])

  const renderDescription = () => {
    if (!description) return null
    if (Array.isArray(description)) {
      return description.map((v, index) => (
        <div key={index} className="TitledCard-description">
          {v}
        </div>
      ))
    } else {
      return <div className="TitledCard-description">{description}</div>
    }
  }

  const _onBack = () => {
    if (onBack) {
      onBack()
    } else if (backUrl) {
      window.location.href = backUrl
    }
  }

  return (
    <StyledTitledCard className="TitledCard-root">
      <div className="TitledCard-shadow"></div>
      <Paper className={classnames('TitledCard-card', { 'TitledCard-card-narrow': viewMode === 'narrow' }, className)}>
        <div tabIndex={-1} id={SKIP_COMMAND_RESET_ID}></div>
        {header}
        {contentBeforeTitle}
        {title && (
          <div className={classnames('TitledCard-title', { left: typeof onBack === 'function' || backUrl })}>
            {(backUrl || onBack) && (
              <div
                className="TitledCard-iconBack"
                onClick={_onBack}
                onKeyDown={e => {
                  // temporary until util function provided
                  if (e.key === 'Enter') {
                    _onBack()
                  }
                }}
                tabIndex={0}
                role="button"
                // eslint-disable-next-line @godaddy/react-intl/id-missing
                aria-label={intl.formatMessage({ id: 'embers.button.back', defaultMessage: 'Back' })}
              >
                <span className={transformRTL('icon-arrow-left', 'icon-arrow-right')}></span>
              </div>
            )}
            <h1>{title}</h1>
          </div>
        )}
        {feedbackMessage}
        {subtitle && <div className="TitledCard-subtitle">{subtitle}</div>}
        {renderDescription()}
        {children}
      </Paper>
    </StyledTitledCard>
  )
}

export default TitledCard
